/**
 * Tiny key/value persistence seam for the anonymous feed engine.
 *
 * Synchronous and JSON-valued — the engine persists inside synchronous marker mutations
 * (markPostRead → broadcast → persist), and the anon dataset is small and hard-bounded by the
 * 30-feed cap, so localStorage fits well. This interface exists so a future IndexedDB-backed
 * implementation (e.g. when adding offline post-content caching) can drop in without touching the
 * engine, and so tests can inject a fake store.
 */
export interface AnonStore {
  /** Parse the JSON stored at `key`, or return `fallback` if absent/corrupt. */
  getJson<T>(key: string, fallback: T): T;
  /** JSON-serialize `value` and store it at `key`. */
  setJson(key: string, value: unknown): void;
}

/** Default {@link AnonStore} backed by `localStorage`. */
export class LocalStorageAnonStore implements AnonStore {
  getJson<T>(key: string, fallback: T): T {
    try {
      const s = localStorage.getItem(key);
      return s ? JSON.parse(s) as T : fallback;
    } catch (e) {
      console.error('anon-store: failed to read', key, e);
      return fallback;
    }
  }

  setJson(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error('anon-store: failed to persist', key, e);
    }
  }
}
